import { FormattedMessage, useIntl } from 'react-intl'
import MarkDownMessage from '@gluedigital/markdown-message'
import { useState, FormEvent, useEffect, useCallback } from 'react'
import { useDialog } from '@hooks/useDialog'
import Loader from '@components/Loader/Loader'
import { useTokenCRUD } from '@hooks/useTokens'
import type { TokenInfo } from 'src/types/auth'

import './CreateTokenDialog.css'

type DialogStep = 'idle' | 'success' | 'error'

interface CreateTokenDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export default function CreateTokenDialog({
  isOpen,
  onClose,
  onSuccess
}: CreateTokenDialogProps) {
  const intl = useIntl()
  const { createToken, refreshTokens } = useTokenCRUD()

  const [step, setStep] = useState<DialogStep>('idle')
  const [isLoading, setIsLoading] = useState(false)
  const [createdToken, setCreatedToken] = useState<TokenInfo | null>(null)
  const [isCopied, setIsCopied] = useState(false)

  const handleClose = useCallback(() => {
    if (step === 'success') {
      refreshTokens()
    }
    onClose()
  }, [step, refreshTokens, onClose])

  const { dialogRef, handleBackdropClick } = useDialog(isOpen, handleClose)

  useEffect(() => {
    if (isOpen) {
      setStep('idle')
      setIsLoading(false)
      setCreatedToken(null)
      setIsCopied(false)
    }
  }, [isOpen])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const name = (formData.get('name') as string)?.trim()

    if (!name) return

    setIsLoading(true)
    try {
      const token = await createToken({ name })
      setCreatedToken(token)
      setStep('success')
      onSuccess?.()
    } catch {
      setStep('error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopy = async () => {
    if (createdToken?.key) {
      await navigator.clipboard.writeText(createdToken.key)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    }
  }

  return (
    <dialog
      id="profile-token-dialog"
      className="app-dialog"
      ref={dialogRef}
      onClick={handleBackdropClick}
      onCancel={(e) => {
        if (isLoading) {
          e.preventDefault()
        } else {
          handleClose()
        }
      }}
    >
      <div className="dialog-inner">
        {step === 'success' && createdToken ? (
          <div className="dialog-step dialog-success-step">
            <h3>
              <FormattedMessage id="profile.token-dialog.success-title" />
            </h3>
            <p className="dialog-description">
              <MarkDownMessage id="profile.token-dialog.success-description" />
            </p>

            <div className="token-key-container">
              <div className="token-key-result">
                <code>{createdToken.key || ''}</code>
              </div>
              <button
                type="button"
                className="secondary-button"
                onClick={handleCopy}
              >
                <FormattedMessage id={isCopied ? 'copied' : 'copy'} />
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="dialog-step">
            <label>
              <h3>
                <MarkDownMessage id="profile.token-dialog.form-label" />
              </h3>
              <input
                name="name"
                type="text"
                placeholder={intl.formatMessage({
                  id: 'placeholder'
                })}
                disabled={isLoading}
                autoFocus
                required
              />
            </label>

            {step === 'error' && (
              <span className="error-message">
                <FormattedMessage id="profile.token-dialog.error" />
              </span>
            )}

            {isLoading ? (
              <Loader mini />
            ) : (
              <button
                type="submit"
                className="highlight-button"
                disabled={isLoading}
              >
                <FormattedMessage id="profile.token-dialog.form-button" />
              </button>
            )}
          </form>
        )}
      </div>
    </dialog>
  )
}
